import { openPaddleCheckout } from './paddle-js'
import type {
  PaymentCheckoutSession,
  PaymentsConfigResponse,
} from '@laplace/contracts'

/**
 * Takes the customer through the provider's checkout for a started payment.
 * Test payments complete through `completeTestPayment` instead (no money
 * moves). Returns the email the customer paid with, when the provider tells.
 *
 * `onProviderCheckout` reports while the provider's own overlay is open: a
 * modal dialog of ours must step aside meanwhile, or its focus trap and
 * `pointer-events: none` on <body> leave the overlay unusable.
 */
export async function payCheckoutSession(
  session: PaymentCheckoutSession,
  config: PaymentsConfigResponse,
  options: {
    completeTestPayment: () => Promise<unknown>
    email?: string | null
    locale?: string
    onProviderCheckout?: (open: boolean) => void
  },
): Promise<{ email: string | null }> {
  if (session.provider === 'fake') {
    await options.completeTestPayment()
    return { email: null }
  }
  if (!config.paddle) throw new Error('Payment provider is not configured')
  options.onProviderCheckout?.(true)
  try {
    return await openPaddleCheckout(config.paddle, session.transactionId, {
      email: options.email,
      locale: options.locale,
    })
  } finally {
    options.onProviderCheckout?.(false)
  }
}

/**
 * Polls until the server confirms a payment (the provider's webhook may
 * arrive a little after the overlay closes). Resolves with the last value;
 * `done` false means it timed out while still pending.
 */
export async function waitForConfirmation<T>(
  read: () => Promise<T>,
  isDone: (value: T) => boolean,
  options: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<{ value: T; done: boolean }> {
  const intervalMs = options.intervalMs ?? 1500
  const deadline = Date.now() + (options.timeoutMs ?? 60_000)
  for (;;) {
    const value = await read()
    if (isDone(value)) return { value, done: true }
    if (Date.now() + intervalMs > deadline) return { value, done: false }
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }
}
